import { useState, useEffect, useCallback } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  Fade,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import liff from "@line/liff";
import { useTranslation } from "react-i18next";

import StyledButton from "default/components/StyledButton";
import { useUrlParams } from "works/higurashi/hooks/useUrlParams";

import { COLORS } from "constants/colors";
import HowToUseSlider from "default/components/HowToUseSlider";
import AppLayout from "works/higurashi/components/AppLayout";
import ButtonDialog from "default/components/ButtonDialog";
import Header from "../components/Header";
import { getGuidelinesUrl, getRequestFormUrl } from "constants/links";
import {
  getGuidelinesAgreementStatus,
  postGuidelinesAgreement,
} from "works/higurashi/utils/api";

const HOW_TO_QA = [
  {
    question:
      "対面での交流を前提にしてもいいですか？そもそもどうやってチャットできているのか、設定を知りたいです。",
    answer:
      "SoulChatでは、より作品の世界観に沿った形での交流体験を提供することを目指しております。\n\n本作品においては、キャラクターと対面で会話している前提でお楽しみいただくことで、作品の世界観により自然に入り込めるように制作しております。\n\n一方で、会話が行われている具体的な状況や場所については、作品の世界観と矛盾しない範囲で、ユーザー様ご自身の解釈に委ねております。",
  },
  {
    question: "同じ人物としてキャラクターたちと接するべきですか？",
    answer:
      "物語はつながって進行するため、あなた自身の設定は統一して楽しむことをおすすめします。キャラクターごとに大きく異なると、会話や関係性に違和感が出る場合があります。",
  },
  {
    question: "会話回数に制限はありますか？",
    answer:
      "関係性やタイミングによって会話回数は多少変動しますが、ユーザー様がご利用いただける会話回数には上限がございます。\n\n現在は、3キャラクター合計で1日最低でも60回の会話量が付与される仕様となっております。この最低会話量は全部使わなかった場合には繰り越されます。チケットを継続購入した場合にも繰り越されます。\n\nまた、「レナとの交流パス」をご利用の場合は、レナとの会話のみで1日最低でも30回の会話料が付与され、同様に繰り越されます。",
  },
  {
    question: "話の途中で、急に返信が来なくなりました。どうしたらいいですか？",
    answer:
      "会話の途中で離席していないにもかかわらず返信が来なくなった場合は、少し時間をおいてから、あらためて話しかけてみてください。\n\nこの場合でも、直前に送信されたメッセージ自体はキャラクターに届いています。また、返信がなかった分については、ユーザー様に付与される最低会話量としては消費されません。",
  },
  {
    question:
      "リセットすると全キャラクターがリセットされますか？",
    answer:
      "現在理由中のチケットに含まれるすべてのキャラクターにおける記憶、クエスト履歴等が初期状態に戻ります。ただし、最低会話量はリセットされません。",
  },
  {
    question: "離席直後に追いLINEをしても良いですか？",
    answer:
      "離席後、キャラクターから返信がなかなかこない場合、いつ返信が返ってくるかは関係性やタイミングによって変動します。離席後こちらから声をかけても仕様上問題ございません。",
  },
  {
    question:
      "クエストがクリア判定されません。クエストクリアが誤判定されます。",
    answer:
      "AIの性能により、クリア判定結果が正確に判定されない場合がございます。もし誤判定されたと思われる場合には「クエスト」ページにある「クリア判定依頼」よりフォームを提出してください。",
  },
  {
    question: "深夜ですが、キャラクターから返事が返ってきません。",
    answer:
      "キャラクターはいつでも返信できるわけではありません。キャラクターの都合やあなたとの関係性に応じて返信が返ってこないこともあります。長い間返信がこない場合には、こちらから声をかけてください。",
  },
  {
    question:
      "他のユーザーたちのチャット内容が学習されキャラクターの挙動が変化する仕様ですか？",
    answer:
      "いいえ、SoulChat におけるキャラクターの挙動は、完全にユーザーごとに独立している仕様となっており、ユーザー様とキャラクター間の会話内容は、他のユーザーの会話内容に影響されません。",
  },
  {
    question: "テキストの入力するキーボードが見えません。",
    answer: "トークルームの左下にあるキーボードマークをタップしてください。",
  },
  {
    question: "交流期間内にクエストをクリアできなかった場合どうなるんですか？",
    answer:
      "交流期間が終了すると現れる、続けて交流するチケットで引き続き交流することが可能になります。",
  },
  {
    question: "PayPayの決済に失敗してしまいます。",
    answer:
      "再度購入ページに進んでいただいた際に出てくる「購入状況を確認」ボタンをタップし、完了処理を進めてくださいませ。もしそれでも問題が解消しない場合には、サポートチームまでお問い合わせください。",
  },
  {
    question:
      "そろそろ期間が終わってしまうんですけど、交流を続けるにはどうしたらいいですか？",
    answer:
      "交流期間が終了した後に、継続交流のためのチケットを購入できます。交流期間が終われば「チケット購入」ページに継続購入チケットが現れます。",
  },
  {
    question: "機種変更してもデータは引き継がれますか？",
    answer:
      "携帯の機種を変更しても、LINEアカウントが変更されない限り、お客様のデータは残るようになっております。記憶もそのままとなります。\n\nただ、お客様のトークルーム内の会話が残るためには、LINEで推奨する移行手続きを正しく行う必要がございます。\n以下のようなページを参照し、LINEを正しく引き継ぐことができれば、SoulChatは全て正しく引き継がれることになります。\n\nhttps://guide.line.me/ja/migration/\n\nなお、万が一、LINEアカウントの移行が正しく行われなかった場合には、運営までにご連絡いただけますと、別途対応をさせていただきたく存じますので、お気軽にご連絡くださいませ。",
  },
  {
    question: "作品に描かれていない内容は正式な情報ですか？",
    answer:
      "いいえ。作品情報と異なる内容や、作品内で描かれていない内容、または作品情報から明確に推測できない内容は、お客様との会話文脈等をもとにAIが生成したものであり、公式な設定・公式コンテンツではございません。\n\n万が一、作品情報と異なる内容が出力された場合は、その旨をご指摘いただけますと、以後の会話に正しく反映されるよう努めます。",
  },
];

// Custom hook for guidelines management
const useGuidelines = (accountKey) => {
  const [openGuidelineDialog, setOpenGuidelineDialog] = useState(false);
  const [agreeLoading, setAgreeLoading] = useState(false);

  const checkGuidelinesStatus = useCallback(async () => {
    try {
      const status = await getGuidelinesAgreementStatus(accountKey);
      if (status === false) {
        setOpenGuidelineDialog(true);
      }
    } catch (error) {
      console.error("Failed to check guidelines status:", error);
    }
  }, [accountKey]);

  const handleAgreeGuideline = async () => {
    setAgreeLoading(true);
    try {
      await postGuidelinesAgreement(accountKey);
      setOpenGuidelineDialog(false);
    } catch (error) {
      console.error("Failed to agree to guidelines:", error);
    } finally {
      setAgreeLoading(false);
    }
  };

  useEffect(() => {
    if (accountKey) {
      checkGuidelinesStatus();
    }
  }, [accountKey, checkGuidelinesStatus]);

  return {
    openGuidelineDialog,
    agreeLoading,
    handleAgreeGuideline,
    setOpenGuidelineDialog,
  };
};

const GuidelinesButton = ({ t, onGuideLine }) => (
  <StyledButton
    variant="contained"
    sx={{ marginTop: 6 }}
    onClick={onGuideLine}
  >
    {t("common:HowToUse.GuideLines")}
  </StyledButton>
);

const BackToChatButton = ({ t, onCloseWindow }) => (
  <StyledButton
    variant="contained"
    sx={{ marginTop: 4 }}
    onClick={onCloseWindow}
  >
    {t("common:button.back_to_chat")}
  </StyledButton>
);

const SupportButtons = ({ t, onContact, onRequestForm, requestFormUrl }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      pt: 4,
    }}
  >
    {requestFormUrl && (
      <StyledButton
        variant="contained"
        sx={{ marginTop: 0, marginBottom: 4 }}
        onClick={onRequestForm}
      >
        {t("common:button.request_form")}
      </StyledButton>
    )}
    <StyledButton variant="contained" sx={{ marginTop: 0 }} onClick={onContact}>
      {t("common:HowToUse.Contact")}
    </StyledButton>
  </Box>
);

const QASection = ({ children }) => (
  <Box
    component="section"
    sx={{
      width: "100%",
      px: { xs: 2, sm: 3 },
      pt: 6,
      pb: 1,
      boxSizing: "border-box",
    }}
  >
    <Header title="Q&A" highlightedWord="Q" />
    <Box
      sx={{
        width: "100%",
        maxWidth: 720,
        mx: "auto",
      }}
    >
      {HOW_TO_QA.map((item, index) => (
        <Accordion
          key={index}
          disableGutters
          elevation={0}
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            color: "white",
            borderRadius: "8px",
            overflow: "hidden",
            "&:before": {
              display: "none",
            },
            "&.Mui-expanded": {
              margin: 0,
            },
          }}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon sx={{ color: "rgba(255, 255, 255, 0.72)" }} />
            }
            sx={{
              px: 2,
              py: 0.5,
              minHeight: 56,
              "&.Mui-expanded": {
                minHeight: 56,
              },
              "& .MuiAccordionSummary-content": {
                my: 1.5,
              },
              "& .MuiAccordionSummary-content.Mui-expanded": {
                my: 1.5,
              },
            }}
          >
            <Typography
              sx={(theme) => ({
                fontFamily: theme.system.fonts.body,
                fontSize: "14px",
                fontWeight: 700,
                lineHeight: 1.6,
                color: theme.system.colors.text.primary,
              })}
            >
              Q. {item.question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
            <Typography
              sx={(theme) => ({
                whiteSpace: "pre-line",
                fontFamily: theme.system.fonts.body,
                fontSize: "12px",
                lineHeight: 1.8,
                color: theme.system.colors.text.secondary,
              })}
            >
              {item.answer}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
    {children}
  </Box>
);

// Component for guidelines dialog
const GuidelinesDialog = ({ open, t, guidelinesUrl, agreeLoading, onAgree }) => (
  <ButtonDialog
    open={open}
    title={t("common:HowToUse.BeforeService")}
    showCloseButton={false}
    content={
      <>
        <Typography
          sx={(theme) => ({
            textAlign: "center",
            fontSize: 13,
            color: theme.typography.color,
            fontFamily: theme.typography.fontFamily,
            whiteSpace: "pre-line",
          })}
        >
          {t("common:HowToUse.GuidelineNotice")}
        </Typography>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: 24,
            marginBottom: 24,
          }}
        >
          <a
            href={guidelinesUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: COLORS.SKY_BLUE,
              textDecoration: "none",
              fontWeight: 500,
              fontSize: 15,
            }}
          >
            {t("common:Footer.guidelines")}
          </a>
        </div>
      </>
    }
    rawButtons={[
      <StyledButton
        key="agree"
        variant="contained"
        sx={{ width: "100%" }}
        onClick={onAgree}
        disabled={agreeLoading}
      >
        {t("common:button.confirmed")}
      </StyledButton>,
    ]}
    handleClose={() => {}}
  />
);

function HowToUse() {
  const { accountKey, lng, work } = useUrlParams();
  const { t } = useTranslation(["common", work]);
  const requestFormUrl = getRequestFormUrl(lng);
  const guidelinesUrl = getGuidelinesUrl(work, lng);

  const { openGuidelineDialog, agreeLoading, handleAgreeGuideline } =
    useGuidelines(accountKey);

  const handleNavigateToGuideLine = () => {
    window.open(guidelinesUrl, "_blank");
  };

  const handleCloseWindow = () => {
    liff.closeWindow();
  };

  const handleNavigateToRequestForm = () => {
    window.open(requestFormUrl, "_blank");
  };

  const handleNavigateToContact = () => {
    window.open("https://line.me/R/ti/p/@314jdaww", "_blank");
  };

  return (
    <AppLayout page="HowToUse">
      <Box
        sx={{
          display: "flex",
          paddingTop: 4,
          paddingBottom: 4,
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Fade in timeout={600}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Header
              title="How To"
              highlightedWord="T"
              sx={{ marginBottom: 4 }}
            />
          </Box>
        </Fade>

        <Fade in timeout={800} style={{ transitionDelay: "200ms" }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <HowToUseSlider work={work} />
          </Box>
        </Fade>

        <Fade in timeout={1000} style={{ transitionDelay: "400ms" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <GuidelinesButton
              t={t}
              onGuideLine={handleNavigateToGuideLine}
            />
            <BackToChatButton t={t} onCloseWindow={handleCloseWindow} />
            <QASection>
              <SupportButtons
                t={t}
                onContact={handleNavigateToContact}
                onRequestForm={handleNavigateToRequestForm}
                requestFormUrl={requestFormUrl}
              />
            </QASection>
          </Box>
        </Fade>

        <GuidelinesDialog
          open={openGuidelineDialog}
          t={t}
          guidelinesUrl={guidelinesUrl}
          agreeLoading={agreeLoading}
          onAgree={handleAgreeGuideline}
        />
      </Box>
    </AppLayout>
  );
}

export default HowToUse;
